import { Injectable } from '@angular/core';

import { IObjectStringKeyMap } from '@shared/models';
import { PropertyService, UtilsService } from '@services';
import { HealthEvent, StateEvent } from '@services/assets/asset.interfaces';
import { IN_USE_STATUSES, stateEventsAsHealthEvents } from '@services/property/in-use-state';
import { IPropertyMeta } from '@services/property/property-model.interfaces';
import { TimeDisplayService, TimeDisplayMode } from '@services/timeDisplay/time-display.service';
import {
  StateHistoryPeriodValue,
  StateHistoryHealthEvent,
  AssetStateHistoryEntity,
  StateHistoryPeriod,
  AssetStateHistoryPeriodEntity,
  PeriodDateRange,
  StateBreakdown,
  PeriodHistoryState,
} from './../model/asset-state.interfaces';

import moment from 'moment';
import {
  each,
  filter,
  find,
  findIndex,
  findLast,
  findLastIndex,
  flatten,
  includes,
  last,
  map,
  mapValues,
  orderBy,
  pickBy,
  some,
  sumBy
} from 'lodash';

/** Colour and title per state value: PropertyService.aiStatuses for health, IN_USE_STATUSES for asset:inUse. */
type StateStatuses = { [state: number]: { type: string; title: string } };

export interface InUseUtilization {
  inUseSeconds: number;
  knownSeconds: number;
  percent: number;
}

/**
 * CVX-6532: the share of an asset:inUse entity's known-state time spent In Use, from the same
 * seconds its State Breakdown displays, so the two agree in every time display mode. Time with no
 * known state (before the first event, or after now) is in neither total. Null when none is known.
 */
export function inUseUtilization(entity: AssetStateHistoryEntity): InUseUtilization | null {
  const inUseSeconds = entity?.stateSeconds?.[1] ?? 0;
  const knownSeconds = inUseSeconds + (entity?.stateSeconds?.[0] ?? 0);
  return knownSeconds ? { inUseSeconds, knownSeconds, percent: Math.round(100 * inUseSeconds / knownSeconds) } : null;
}

@Injectable({
  providedIn: 'root'
})
export class AssetStatusService {
  constructor(
    private propertyService: PropertyService,
    private utilsService: UtilsService,
    private timeDisplayService: TimeDisplayService
  ) {}

  public buildStates(
    healthEvents: IObjectStringKeyMap<HealthEvent[]>,
    timespan: string,
    dateRange?: PeriodDateRange,
    periodsByDateRange?: boolean,
    assetLocationID?: number,
    statuses: StateStatuses = this.propertyService.aiStatuses
  ): AssetStateHistoryEntity[] {
    return filter(map(healthEvents, (events, propertyKey) => {
      const propertyId: number = +propertyKey;
      const property = this.propertyService.getPropertyById(propertyId);

      if (property?.disabledAt === 0 || propertyId === 0) {
        return this.buildState(events, propertyId, timespan, dateRange, periodsByDateRange, assetLocationID, statuses);
      }
    }));
  }

  /** asset:inUse state history (getAssets stateEvents), one entity per property, kept apart from health. */
  public buildInUseStates(stateEvents: IObjectStringKeyMap<StateEvent[]>, timespan: string, dateRange?: PeriodDateRange, periodsByDateRange?: boolean, assetLocationID?: number): AssetStateHistoryEntity[] {
    return this.buildStates(stateEventsAsHealthEvents(stateEvents), timespan, dateRange, periodsByDateRange, assetLocationID, IN_USE_STATUSES);
  }

  /**
   * CVX-6583: the events of the properties the asset shows, for its detail page; reports keep every
   * property. propertyID 0, the asset's overall health, always stays.
   */
  public visibleEvents<T>(events: IObjectStringKeyMap<T[]>, selectedProperties: IPropertyMeta[] = []): IObjectStringKeyMap<T[]> {
    return pickBy(events, (_, propertyKey) => {
      const property = this.propertyService.getPropertyById(+propertyKey);
      return !property || !this.propertyService.isPropertyHiddenFor(property, selectedProperties);
    });
  }

  /**
   * CVX-6532: In Use utilization of one asset:inUse property over the selected period, built the
   * way StateHistoryComponent builds its rows and formatted like its State Breakdown totals.
   */
  public inUseRing(stateEvents: StateEvent[], propertyID: number, timespan: string, dateRange?: PeriodDateRange, assetLocationID?: number) {
    const [entity] = this.buildInUseStates({ [propertyID]: stateEvents ?? [] }, timespan, dateRange, false, assetLocationID);
    const utilization = inUseUtilization(entity);
    if (!utilization) {
      return null;
    }
    const trimHours = includes(['day', 'yesterday'], timespan);
    const timeFormat = trimHours ? 'mm:ss' : 'mm';
    return {
      ...utilization,
      inUse: this.utilsService.secsToDuration(utilization.inUseSeconds, trimHours, timeFormat),
      known: this.utilsService.secsToDuration(utilization.knownSeconds, trimHours, timeFormat)
    };
  }

  private buildState(healthEvents: HealthEvent[], propertyKey: number, timespan: string, periodDateRange: PeriodDateRange, periodsByDateRange: boolean, assetLocationID: number, statuses: StateStatuses): AssetStateHistoryEntity {
    const dateRange = periodDateRange || this.utilsService.timespan(timespan);
    const indexedHealthEvents: StateHistoryHealthEvent[] = this.indexHealthEvents(healthEvents);
    const stateHistoryPeriods: StateHistoryPeriod[] = periodsByDateRange ? this.buildPeriodsByDateRange(dateRange) : this.buildPeriodsByTimespan(timespan, dateRange);
    this.defineEventsBasedOnPeriods(stateHistoryPeriods, indexedHealthEvents);

    return this.getStateHistoryEntity(stateHistoryPeriods, propertyKey, timespan, statuses, assetLocationID);
  }

  private indexHealthEvents(healthEvents: HealthEvent[]): StateHistoryHealthEvent[] {
    const events = filter(orderBy(healthEvents, 'time'), (event) => {
      return event.health <= 2;
    });

    return map(events, (event, index) => ({
      ...event,
      time: Math.round(event.time * 1000),
      index,
      // Preserve work/clock time seconds for time display mode support
      workTimeSeconds: event.workTimeSeconds || 0,
      clockTimeSeconds: event.clockTimeSeconds || 0
    }));
  }

  private buildPeriodsByDateRange(dateRange: PeriodDateRange): StateHistoryPeriod[] {
    const startTimeValue = moment(dateRange.startTime).valueOf();
    const endTimeValue = moment(dateRange.endTime).valueOf();

    return [{
      range: { startTime: startTimeValue, endTime: endTimeValue },
      title: moment(startTimeValue).format(),
      events: [],
      states: []
    }];
  }

  private buildPeriodsByTimespan(timespanKey: string, dateRange: PeriodDateRange): StateHistoryPeriod[] {
    const periods: StateHistoryPeriod[] = [];
    const daysDiff = moment(dateRange.endTime).diff(dateRange.startTime, 'days');
    const monthsDiff = moment(dateRange.endTime).diff(dateRange.startTime, 'months');
    const yearsDiff = moment(dateRange.endTime).diff(dateRange.startTime, 'year');

    if (timespanKey === 'custom') {
      if (daysDiff === 0) {
        timespanKey = 'day';
      } else if (daysDiff <= 7) {
        timespanKey = 'week';
      } else if (daysDiff <= 31) {
        timespanKey = 'month';
      } else if (daysDiff <= 91) {
        timespanKey = 'quarter';
      } else if (monthsDiff < 6) {
        timespanKey = 'halfYear';
      } else if (yearsDiff < 6) {
        timespanKey = 'year';
      } else {
        timespanKey = 'all';
      }
    }

    if (timespanKey === 'week') {
      for (let i = 0; i <= 6; i++) {
        let startTimeValue = moment(dateRange.startTime).add(i, 'day').startOf('day').valueOf();
        let endTimeValue = moment(dateRange.startTime).add(i, 'day').endOf('day').valueOf();

        periods.push({
          range: { startTime: startTimeValue, endTime: endTimeValue },
          title: moment(startTimeValue).format('dd')[0],
          events: [],
          states: []
        });
      }
    } else if (includes(['day', 'yesterday'], timespanKey)) {
      for (let i = 0; i <= 1; i++) {
        let startTimeValue = moment(dateRange.startTime).startOf('day').add(i * 12, 'hour').valueOf();
        let endTimeValue = moment(dateRange.startTime).startOf('day').add((i + 1) * 12, 'hour').valueOf() - 1;

        periods.push({
          range: { startTime: startTimeValue, endTime: endTimeValue },
          title: `${i * 12}:00`,
          events: [],
          states: []
        });
      }
    } else if (timespanKey === 'month') {
      for (let i = 0; i <= 3; i++) {
        let startTimeValue = moment(dateRange.startTime).startOf('month').add(i, 'week').valueOf();
        let endTimeValue = moment(dateRange.startTime).startOf('month').add(i + 1, 'week').valueOf();

        const startMoment = moment(startTimeValue);
        const endMoment = moment(endTimeValue);
        const title = `${startMoment.format('MMM')} ${startMoment.format('DD')}-${endMoment.format('DD')}`;

        periods.push({
          range: {startTime: startTimeValue, endTime: endTimeValue},
          title,
          events: [],
          states: []
        });
      }
    } else if (timespanKey === 'quarter') {
      for (let i = 0; i <= 2; i++) {
        let startTimeValue = moment(dateRange.startTime).startOf('quarter').add(i, 'month').valueOf();
        let endTimeValue = moment(dateRange.startTime).startOf('quarter').add(i + 1, 'month').valueOf();

        periods.push({
          range: { startTime: startTimeValue, endTime: endTimeValue },
          title: `${moment(startTimeValue).format('MMM')}`,
          events: [],
          states: []
        });
      }
    } else if (includes(['30days', '60days', '90days'], timespanKey)) {
      let periodLimit: number = +timespanKey[0];
      let dayAmount: number = 10;

      if (timespanKey === '90days') {
        periodLimit = 6;
        dayAmount = 15;
      }

      for (let i = 0; i < periodLimit; i++) {
        let startTimeValue = moment(dateRange.startTime).startOf('day').add(i * dayAmount, 'day').valueOf();
        let endTimeValue = moment(dateRange.startTime).startOf('day').add((i + 1) * dayAmount, 'day').valueOf();

        const startMoment = moment(startTimeValue);
        const endMoment = moment(endTimeValue);
        const title = `${startMoment.format('MMM')} ${startMoment.format('DD')}-${endMoment.format('DD')}`;

        periods.push({
          range: {startTime: startTimeValue, endTime: endTimeValue},
          title,
          events: [],
          states: []
        });
      }
    } else if (timespanKey === 'halfYear') {
      for (let i = 0; i <= monthsDiff; i++) {
        let startTimeValue = moment(dateRange.startTime).add(i, 'month').valueOf();
        let endTimeValue = moment(dateRange.startTime).add(i + 1, 'month').valueOf();

        periods.push({
          range: { startTime: startTimeValue, endTime: endTimeValue },
          title: `${moment(startTimeValue).format('MMM Y')}`,
          events: [],
          states: []
        });
      }
    } else if (timespanKey === 'year') {
      for (let i = 0; i <= yearsDiff; i++) {
        let startTimeValue = moment(dateRange.startTime).add(i, 'year').valueOf();
        let endTimeValue = moment(dateRange.startTime).add(i + 1, 'year').valueOf();

        periods.push({
          range: { startTime: startTimeValue, endTime: endTimeValue },
          title: `${moment(startTimeValue).format('MMM Y')}`,
          events: [],
          states: []
        });
      }
    } else if (timespanKey === 'all') {
      const maxPeriod = 6;
      const timeDiff = dateRange.endTime - dateRange.startTime;
      const timeInPeriod = timeDiff / maxPeriod;

      for (let i = 0; i <= maxPeriod; i++) {
        let startTimeValue = dateRange.startTime + (timeInPeriod * i);
        let endTimeValue = dateRange.startTime + (timeInPeriod * (i + 1));

        periods.push({
          range: { startTime: startTimeValue, endTime: endTimeValue },
          title: `${moment(startTimeValue).format('MMM Y')}`,
          events: [],
          states: []
        });
      }
    }

    return periods;
  }

  private defineEventsBasedOnPeriods(stateHistoryPeriods: StateHistoryPeriod[], stateEvents: StateHistoryHealthEvent[]) {
    each(stateHistoryPeriods, (period) => {
      each(stateEvents, (event) => {
        if (event.time >= period.range.startTime && event.time <= period.range.endTime) {
          period.events.push(event);
        }
      });

      this.definePeriodStatesInRange(period, stateEvents);
    });
    this.definePeriodStatesForEmptyPeriods(stateHistoryPeriods, stateEvents);
  }

  private definePeriodStatesForEmptyPeriods(stateHistoryPeriods: StateHistoryPeriod[], stateEvents: StateHistoryHealthEvent[]) {
    const currentTime = moment().valueOf();

    each(stateHistoryPeriods, (period: StateHistoryPeriod, index) => {
      if (period?.events?.length === 0 && period.range.startTime < currentTime) {
        const lastEvent = findLast(stateEvents, (event) => {
          return event.time < period.range.startTime;
        });

        if (lastEvent) {
          const endTime = currentTime > period.range.endTime ? period.range.endTime : currentTime;
          period.events.push(lastEvent);
          period.states.push({
            health: lastEvent.health,
            seconds: Math.round((endTime - period.range.startTime) / 1000),
            startTimestamp: period.range.startTime,
            endTimestamp: endTime
          });
        }
      }
    });
  }

  private definePeriodStatesInRange(period: StateHistoryPeriod, stateEvents: StateHistoryHealthEvent[]) {
    each(period.events, (event, index: number) => {
      const nextEvent = period.events[index + 1];
      if (period.states.length === 0) {
        const previousEvent = find(stateEvents, { index: event.index - 1 });

        if (previousEvent) {
          // no workTimeSeconds: event.workTimeSeconds covers the whole previous state, which began
          // before this period; stateWorkSeconds works out the part inside it
          period.states.push({
            health: previousEvent.health,
            seconds: Math.round((event.time - period.range.startTime) / 1000),
            startTimestamp: period.range.startTime,
            endTimestamp: event.time
          });
        }
      }

      if (nextEvent) {
        period.states.push({
          health: event.health,
          seconds: Math.round((nextEvent.time - event.time) / 1000),
          startTimestamp: event.time,
          endTimestamp: nextEvent.time,
          // nextEvent.workTimeSeconds is the work time from event to nextEvent
          workTimeSeconds: nextEvent.workTimeSeconds || 0
        });
      } else {
        const currentTime = moment().valueOf();
        let endData = period.range.endTime;

        if (endData > currentTime) {
          endData = currentTime;
        }

        period.states.push({
          health: event.health,
          seconds: Math.round((endData - event.time) / 1000),
          startTimestamp: event.time,
          endTimestamp: endData
          // No workTimeSeconds for ongoing segment - will be calculated client-side
        });
      }
    });
  }

  private getStateHistoryEntity(eventPeriods: StateHistoryPeriod[], propertyKey: number, timespan: string, statuses: StateStatuses, assetLocationID?: number): AssetStateHistoryEntity {
    const currentState: AssetStateHistoryEntity = { statesBreakdown: [], stateHistoryPeriods: []};

    this.defineStateHistoryItems(eventPeriods, currentState, propertyKey, statuses, assetLocationID, timespan);
    this.defineStatesBreakdown(eventPeriods, currentState, timespan, statuses, assetLocationID);

    return currentState;
  }

  private defineStateHistoryItems(eventPeriods: StateHistoryPeriod[], currentState: AssetStateHistoryEntity, propertyKey: number, statuses: StateStatuses, assetLocationID?: number, timespan?: string) {
    const currentTime = moment().valueOf();
    const inRangePeriodIndex = findIndex(eventPeriods, (period) => {
      return currentTime > period.range.startTime && currentTime < period.range.endTime;
    });
    const timeDisplayMode = this.timeDisplayService.getMode();

    // Match the format used in state breakdown section
    let timeFormat: string = 'mm:ss';
    let trimHours = true;
    if (!includes(['day', 'yesterday'], timespan)) {
      trimHours = false;
      timeFormat = 'mm';
    }

    each(eventPeriods, (period, periodIndex) => {
      const lastStateIndex = findLastIndex(period.states, 'seconds');
      const isInRangePeriod = inRangePeriodIndex === periodIndex;

      const states: StateHistoryPeriodValue[] = map(period.states, (state, stateIndex) => {
        const isLastState = lastStateIndex === stateIndex;

        const displaySeconds = timeDisplayMode === TimeDisplayMode.Work ? this.stateWorkSeconds(state, assetLocationID) : state.seconds;

        const stateValue: StateHistoryPeriodValue = {
          color: statuses[state.health]?.type,
          value: state.seconds,
          tooltipLabel: this.utilsService.secsToDuration(displaySeconds, trimHours, timeFormat),
          health: state.health,
          range: period.range
        };

        if (state.seconds && isInRangePeriod && isLastState && period.range.endTime > currentTime) {
          stateValue.startTime = {
            seconds: Math.round((currentTime / 1000) - state.seconds),
            trimHours: true,
            format: 'mm:ss'
          };
        }

        return stateValue;
      });

      let title = '';

      if (propertyKey) {
        const property = this.propertyService.getPropertyById(propertyKey);

        if (periodIndex === 0 && property) {
          title = property.title;
        }
      } else {
        title = period.title;
      }

      const stateHistoryItem: AssetStateHistoryPeriodEntity = { title, range: period.range };

      if (states.length) {
        stateHistoryItem.period = states;
      }

      currentState.stateHistoryPeriods.push(stateHistoryItem);
    });

    this.defineCurrentPeriodHistoryItems(eventPeriods, currentState);
  }

  private defineCurrentPeriodHistoryItems(eventPeriods: StateHistoryPeriod[], currentState: AssetStateHistoryEntity) {
    const currentStateHistoryIndex = findLastIndex(currentState.stateHistoryPeriods, (stateHistoryPeriod) => {
      return some(stateHistoryPeriod.period, 'startTime');
    });

    if (currentState.stateHistoryPeriods[currentStateHistoryIndex]) {
      const currentStateHistory = currentState.stateHistoryPeriods[currentStateHistoryIndex];
      const currentEventPeriod = eventPeriods[currentStateHistoryIndex];

      const timeRange = currentEventPeriod.range;
      const secondsInRange = Math.round((timeRange.endTime - timeRange.startTime) / 1000);
      const lastEvent = last(currentEventPeriod.events);
      const isEventInRange = lastEvent.time >= timeRange.startTime && lastEvent.time <= timeRange.endTime;
      const periods = currentStateHistory.period as StateHistoryPeriodValue[];

      if (isEventInRange) {
        const currentPeriod = find(periods, 'startTime');
        const leftSecondsAfterCurrentPeriod = Math.round((timeRange.endTime - lastEvent.time) / 1000) - currentPeriod.value;

        periods.push({
          value: leftSecondsAfterCurrentPeriod,
          tooltipLabel: null
        });

        const periodSeconds = sumBy(periods, 'value');
        const previousPeriodExists = periodSeconds >= secondsInRange;

        if (!previousPeriodExists) {
          const secondsBeforeFirstPeriod = secondsInRange - periodSeconds;

          periods.unshift({
            value: secondsBeforeFirstPeriod,
            tooltipLabel: null
          });
        }
      } else {
        const periodSeconds = sumBy(periods, 'value');

        periods.push({
          value: secondsInRange - periodSeconds,
          tooltipLabel: null
        });
      }
    }
  }

  private defineStatesBreakdown(eventPeriods: StateHistoryPeriod[], currentState: AssetStateHistoryEntity, timespan: string, statuses: StateStatuses, assetLocationID?: number) {
    let timeFormat: string = 'mm:ss';
    let trimHours = true;

    if (!includes(['day', 'yesterday'], timespan)) {
      trimHours = false;
      timeFormat = 'mm';
    }

    const timeDisplayMode = this.timeDisplayService.getMode();
    const states = flatten(map(eventPeriods, 'states'));
    const lastState = last(states);

    // Calculate clock time per state from period states (health 0/1/2, or inUse 0/1)
    const clockTimeMap = mapValues(statuses, () => 0);
    each(states, (state) => {
      clockTimeMap[state.health] += state.seconds;
    });

    // Work time per state over the same period states, so both modes measure the same window
    const workTimeMap = mapValues(statuses, () => 0);

    if (timeDisplayMode === TimeDisplayMode.Work || timeDisplayMode === TimeDisplayMode.Both) {
      each(states, (state) => {
        workTimeMap[state.health] += this.stateWorkSeconds(state, assetLocationID);
      });
    }

    // CVX-6532: the seconds behind each displayed total (work in Work mode, else clock)
    currentState.stateSeconds = timeDisplayMode === TimeDisplayMode.Work ? { ...workTimeMap } : { ...clockTimeMap };

    each(clockTimeMap, (clockValue, health) => {
      const workSeconds = workTimeMap[health];
      let displayValue: string;

      if (timeDisplayMode === TimeDisplayMode.Work) {
        displayValue = this.utilsService.secsToDuration(workSeconds, trimHours, timeFormat);
      } else {
        displayValue = this.utilsService.secsToDuration(clockValue, trimHours, timeFormat);
      }

      const stateBreakdown: StateBreakdown = {
        value: displayValue,
        color: statuses[health]?.type,
      };
      // the health legend is colour-only; In Use / Not In Use need their (customizable) term
      if (statuses !== this.propertyService.aiStatuses) {
        stateBreakdown.label = statuses[health]?.title;
      }

      // In "both" mode, add suffixes and work time as secondary value
      if (timeDisplayMode === TimeDisplayMode.Both) {
        stateBreakdown.value = `${displayValue}C`;
        const workFormatted = this.utilsService.secsToDuration(workSeconds, trimHours, timeFormat);
        stateBreakdown.workValue = `${workFormatted}W`;
      }

      const activeValue = timeDisplayMode === TimeDisplayMode.Work ? workSeconds : clockValue;
      if (activeValue && lastState?.health === +health && timespan !== 'yesterday') {
        stateBreakdown.startTime = {
          seconds: Math.round((moment().valueOf() / 1000) - activeValue),
          trimHours,
          format: timeFormat
        };
      }

      currentState.statesBreakdown.unshift(stateBreakdown);
    });
  }

  // Work seconds of one period state: the stored value when the state runs change to change,
  // otherwise (cut at a period edge, or still open) from the schedule - clock time without one
  private stateWorkSeconds(state: PeriodHistoryState, assetLocationID: number): number {
    if (state.workTimeSeconds !== undefined) {
      return state.workTimeSeconds;
    }
    return this.timeDisplayService.calculateWorkTimeBetweenSync(
      Math.round(state.startTimestamp / 1000), Math.round(state.endTimestamp / 1000), assetLocationID || 0
    );
  }

  /**
   * Calculate work time for a specific time range using the schedule data.
   * Uses sync version to avoid async complexity in data building.
   */
  private calculateWorkTimeForRange(startTimestamp: number, endTimestamp: number, locationID: number): number {
    const clockSeconds = endTimestamp - startTimestamp;

    if (!locationID || locationID <= 0) {
      return clockSeconds;
    }

    // Use the sync calculation - it will use cached schedule data if available,
    // or fall back to clock time if not cached yet
    // Note: This calculates from startTimestamp to "now", but we need startTimestamp to endTimestamp
    // So we calculate the work time proportion and apply it

    // For segments that end before "now", we need a different approach
    const nowSeconds = Math.floor(Date.now() / 1000);

    if (endTimestamp >= nowSeconds) {
      // Segment includes current time - use the existing sync method
      return this.timeDisplayService.calculateWorkTimeSinceSync(startTimestamp, locationID);
    }

    // For past segments, calculate work time using the schedule data
    // We need to manually calculate the overlap with working hours
    // For now, use a simple ratio approach based on cached schedule availability
    const workTimeFromStart = this.timeDisplayService.calculateWorkTimeSinceSync(startTimestamp, locationID);
    const workTimeToEnd = endTimestamp < nowSeconds
      ? this.timeDisplayService.calculateWorkTimeSinceSync(endTimestamp, locationID)
      : 0;

    // Work time in the segment = total work since start - work since end
    const segmentWorkTime = Math.max(0, workTimeFromStart - workTimeToEnd);

    // If we got 0 work time but have significant clock time, schedule data may not be available
    // Fall back to clock time in this case
    if (segmentWorkTime === 0 && clockSeconds > 3600) {
      return clockSeconds;
    }

    return segmentWorkTime;
  }
}
